import { useEffect, useState } from "react";
import { RefreshCw, Wifi, WifiOff, Loader2, Network } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";

export function TailscalePanel() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/tailscale").then((r) => setS(r.data)).catch(() => setS({ connected: false, error: "Gagal membaca status" }));
  useEffect(() => { load(); }, []);
  const reconnect = async () => {
    setBusy(true);
    try {
      const r = await api.post("/tailscale/reconnect");
      setS(r.data);
      r.data.connected ? toast.success("Tailscale terhubung") : toast.error(r.data.error || "Belum terhubung");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  if (!s) return null;
  return (
    <section data-testid="tailscale-panel" className="rounded-lg border border-slate-200 bg-white p-4 space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <Network className="h-5 w-5 text-sky-600" />
        <h2 className="font-display text-2xl font-bold">Tailscale</h2>
        <span data-testid="tailscale-status" className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-1 rounded ${s.connected ? "bg-green-50 text-green-600" : "bg-slate-100 text-slate-500"}`}>
          {s.connected ? <Wifi className="h-3.5 w-3.5" /> : <WifiOff className="h-3.5 w-3.5" />}{s.connected ? "TERHUBUNG" : s.configured ? "TIDAK TERHUBUNG" : "BELUM DIATUR"}
        </span>
        {s.node && <span className="text-xs font-mono text-slate-500">{s.node} · <span className="text-sky-600">{s.ip}</span></span>}
        <div className="ml-auto flex gap-2">
          <Button data-testid="tailscale-refresh" variant="outline" size="sm" onClick={load}><RefreshCw className="h-4 w-4 mr-1" />Refresh</Button>
          {s.configured && (
            <Button data-testid="tailscale-reconnect" size="sm" disabled={busy} onClick={reconnect} className="bg-sky-600 hover:bg-sky-700 text-white">
              {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Wifi className="h-4 w-4 mr-1" />}Hubungkan ulang
            </Button>
          )}
        </div>
      </div>
      {s.error && !s.connected && <p data-testid="tailscale-error" className="text-xs font-mono text-red-600 break-all">{s.error}</p>}
      {s.routes?.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 text-xs">
          <span className="text-slate-500">Subnet rumah/kantor:</span>
          {s.routes.map((r) => <span key={r} className="font-mono rounded border border-slate-300 px-2 py-0.5">{r}</span>)}
        </div>
      )}
      {s.peers?.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[11px] uppercase tracking-wider text-slate-500"><tr><th className="text-left py-1">Perangkat</th><th className="text-left">IP Tailscale</th><th className="text-left hidden sm:table-cell">OS</th><th className="text-left hidden sm:table-cell">Rute</th><th className="text-left">Status</th></tr></thead>
            <tbody>
              {s.peers.map((p) => (
                <tr key={p.ip} className="border-t border-slate-100">
                  <td className="py-1.5 font-mono font-semibold">{p.name}</td><td className="font-mono">{p.ip}</td><td className="text-slate-500 hidden sm:table-cell">{p.os}</td>
                  <td className="font-mono text-xs hidden sm:table-cell">{p.routes.join(", ") || "—"}</td>
                  <td className={`text-xs font-semibold ${p.online ? "text-green-600" : "text-slate-400"}`}>{p.online ? "ONLINE" : "OFFLINE"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-xs text-slate-500">Device yang diberi tanda "Lewat Tailscale" dihubungi memakai IP LAN (mis. http://192.168.8.x) selama rutenya tersedia di atas.</p>
    </section>
  );
}
